/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { LogService, NotifyService, UtilsService } from '../../../../../../core/services';
import { NetworkingApiService, NetworkInterfaceModel } from '../../../../shared';
import { Observable } from 'rxjs';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-vlan',
    templateUrl: './add-edit-vlan.component.html'
})
export class AddEditVlanComponent extends BaseComponent {
    /**
     * FormGroup.
     */
    public formGroup: FormGroup;

    /**
     * Store for dropdown content.
     */
    public dropdown: DropdownModel[] = [];

    /**
     * Store the vlan id for editing.
     */
    public editId: string;

    /**
     * Store the interfaces for checks against label.
     * @private
     */
    private storedInterfaces: NetworkInterfaceModel[] = [];

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private networkingApiService: NetworkingApiService,
        private notifyService: NotifyService,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.setupFormGroups();
        this.setUpFormSubscribers();
    }

    /**
     * Add vLan via the API.
     * @private
     */
    public submitVlan(): Observable<any> {
        return new Observable<any>(observer => {
            this.notifyService.muteLocalNotifications();
            const formValues = this.formGroup.value;

            const apiCall = this.editId
                ? this.networkingApiService.editVLAN(this.editId, formValues)
                : this.networkingApiService.addVLAN(formValues);

            apiCall
                .pipe(takeUntil((this.destroyed$)))
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        if (this.editId) {
                            this.notifyService.success('VLAN edited');
                        } else {
                            this.notifyService.success('VLAN created');
                        }
                        const url: string = this.editId ? '../../' : '../';
                        this.router.navigate([url], { relativeTo: this.activatedRoute }).then().catch();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AddVlanComponent: addVLAN() error adding vlan ', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data via the API.
     * @private
     */
    private setUpPageData(): void {
        this.editId = this.activatedRoute.snapshot.paramMap.get('id');
        this.networkingApiService.getInterfaces()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: NetworkInterfaceModel[]): void => {
                    this.storedInterfaces = data;
                    if (this.editId) {
                        this.patchForm(data);
                    }
                    this.dropdown = this.setUpDropdownContent(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddEditVlanComponent:setUpPageData', 'Unable to get page data' + error.message);
                }
            });
    }

    /**
     * Set up dropdown content
     * @param data
     * @private
     */
    private setUpDropdownContent(data: NetworkInterfaceModel[]): DropdownModel[] {
        const blackList: string[] = ['bondChild', 'vlan'];
        const interfaces: NetworkInterfaceModel[] = data.filter(item => !blackList.includes(item.type));

        return this.utilsService.convertArrayToDropDown(interfaces, 'id', 'name');
    }

    /**
     * Setup Floating IP form.
     * @private
     */
    private setupFormGroups(): void {
        this.formGroup = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            type: 'vlan',
            parentId: [null, Validators.required],
            vlanId: [null, Validators.required]
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    this.utilsService.debounce('form', () => {
                        if (this.editId) {
                            if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                                this.formGroup.markAsPristine();
                            } else {
                                this.formGroup.markAsDirty();
                            }
                        }
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditVlanComponent:setUpFormSubscribers() error', error.message);
                }
            });

        const formGroupName: AbstractControl = this.formGroup.get('name');
        formGroupName.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: any): void => {
                    this.utilsService.debounce('check-name', () => {
                        this.utilsService.checkDuplicateFormValue(formGroupName, 'name', this.storedInterfaces, name, this.editId);
                    });
                }
            });

        const formGroupVlanId: AbstractControl = this.formGroup.get('vlanId');
        formGroupVlanId.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (vlanId: any): void => {
                    this.utilsService.debounce('check-name', () => {
                        const listToCheck = this.storedInterfaces.map(item => {
                            return { id: item.id, vlanId: item.settings.vlanId };
                        });
                        this.utilsService.checkDuplicateFormValue(formGroupVlanId, 'vlanId', listToCheck, vlanId, this.editId);
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditVlanComponent:setUpPageData() error', error.message);
                }
            });
    }

    /**
     * Patch form for edit
     * @param data
     * @private
     */
    private patchForm(data: NetworkInterfaceModel[]): void {
        const vlan = data.find((item: any) => item.id === this.editId);
        const patchValues = {
            name: vlan.name,
            type: 'vlan',
            parentId: vlan.settings.parentId,
            vlanId: vlan.settings.vlanId
        };

        this.formGroup.patchValue(patchValues);
        this.initialFormData = this.formGroup.value;
        this.formGroup.get('vlanId').disable();
    }
}
